import React, { useState, useRef } from 'react';
import styled from 'styled-components';

interface FileUploaderProps {
  onFileSelected: (file: File) => void;
  accept?: string;
  maxSizeMB?: number;
}

const FileUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const UploadArea = styled.div`
  width: 100%;
  border: 2px dashed #9333EA;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  background-color: rgba(147, 51, 234, 0.05);
  
  &:hover {
    background-color: rgba(147, 51, 234, 0.1);
  }
`;

const UploadIcon = styled.div`
  margin-bottom: 16px;
`;

const UploadText = styled.p`
  color: white;
  font-size: 14px;
  text-align: center;
  margin: 0;
`;

const UploadSubtext = styled.p`
  color: #A1A1AA;
  font-size: 12px;
  text-align: center;
  margin: 8px 0 0 0;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ErrorMessage = styled.p`
  color: #EF4444;
  font-size: 14px;
  margin: 8px 0 0 0;
  text-align: center;
`;

const FileUploader: React.FC<FileUploaderProps> = ({ 
  onFileSelected, 
  accept = 'video/*', 
  maxSizeMB = 100 
}) => {
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    
    if (file.size > maxSizeBytes) {
      setError(`File size exceeds ${maxSizeMB}MB limit.`);
      return;
    }
    
    setError(null);
    onFileSelected(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    
    if (file.size > maxSizeBytes) {
      setError(`File size exceeds ${maxSizeMB}MB limit.`);
      return;
    }
    
    setError(null);
    onFileSelected(file);
  };

  return (
    <FileUploadContainer>
      <UploadArea 
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <UploadIcon>
          <img src="/upload-icon.svg" alt="Upload" width="32" height="32" />
        </UploadIcon>
        <UploadText>Drag & drop your video here or click to browse</UploadText>
        <UploadSubtext>MP4, MOV or WebM up to {maxSizeMB}MB</UploadSubtext>
      </UploadArea>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <HiddenInput
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept={accept}
      />
    </FileUploadContainer>
  );
};

export default FileUploader;
